/**
 * Attribution snapshot for conversion events
 *
 * Reads every marketing identifier the browser can still see at the moment a
 * conversion fires, and persists nothing of its own. Three cookies are read and
 * none are written: _marketing_ids (ours, refreshed on every pageview by
 * global/cookies.js), _ga (Google's) and ajs_anonymous_id (Segment's).
 *
 * The consequence of not persisting is deliberate. UTMs and the referrer live
 * only on the pageview that carried them, so a submit that happens after the
 * visitor has navigated away from the landing URL reports neither. Those keys
 * come back null rather than guessed — absent means "not knowable from here",
 * never "no campaign". If that gap proves material, the fix is a first-touch
 * cookie added later, not a default value invented now.
 *
 * Click IDs read the URL first and the _marketing_ids slot second, so a browser
 * that blocks our cookie still reports the click that is in front of it.
 *
 * This module is import-safe: no top-level side effects, so bundling it into a
 * component entry cannot re-run the cookie write that global/cookies.js owns.
 *
 * @package Felix
 */

// Duplicated from global/tracking.js rather than imported: that module builds a
// Map for decorating outbound links and pulls the whole link-forwarding system
// in with it. Same five names, different consumer, different shape.
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

// URL param first, then the network's slot in _marketing_ids.
const CLICK_IDS = [
	{ key: 'googleClickID', param: 'gclid', network: 'google' },
	{ key: 'facebookClickID', param: 'fbclid', network: 'facebook' },
	{ key: 'tiktokClickID', param: 'ttclid', network: 'tiktok' },
	{ key: 'redditClickID', param: 'rdt_cid', network: 'reddit' },
	{ key: 'pinterestClickID', param: 'epik', network: 'pinterest' },
]

/**
 * Read a cookie by name.
 * @param {string} name - Cookie name
 * @returns {string|null} Decoded value, or null when absent or unreadable
 */
const readCookie = (name) => {
	try {
		const prefix = `${name}=`
		const cookie = document.cookie
			.split(';')
			.map((part) => part.trim())
			.find((part) => part.startsWith(prefix))

		return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null
	} catch {
		return null
	}
}

/**
 * Parse the _marketing_ids cookie written by global/cookies.js.
 * @returns {Object} Per-network slots, or an empty object when absent or malformed
 */
const readMarketingIds = () => {
	const raw = readCookie('_marketing_ids')
	if (!raw) return {}

	try {
		const parsed = JSON.parse(raw)
		return parsed && typeof parsed === 'object' ? parsed : {}
	} catch {
		return {}
	}
}

/**
 * Read Segment's anonymous id, tolerating the quoted form older versions wrote.
 * @returns {string|null} Anonymous id or null
 */
const readSegmentAnonymousId = () => {
	const value = readCookie('ajs_anonymous_id')
	return value ? value.replace(/^"|"$/g, '') || null : null
}

/**
 * Current pathname, normalised to exactly one trailing slash.
 * The query string is deliberately excluded — its ad params are already carried,
 * typed, in utmParams, and folding them in would make the value high-cardinality.
 * @returns {string} e.g. '/weight-loss-more-info/'
 */
const getPath = () => {
	const pathname = window.location.pathname || '/'
	return pathname.endsWith('/') ? pathname : `${pathname}/`
}

/**
 * Collect the UTM params present on the current URL.
 * @param {URLSearchParams} params - Parsed current query string
 * @returns {Object|null} Object of present params, or null when none are
 */
const getUtmParams = (params) => {
	const utm = {}

	UTM_PARAMS.forEach((name) => {
		const value = params.get(name)
		if (value) utm[name] = value
	})

	return Object.keys(utm).length > 0 ? utm : null
}

/**
 * SHA-256 of an email address, lowercased and trimmed first — the normalisation
 * Meta and Google both require for advanced matching.
 *
 * crypto.subtle is a secure-context API, so this returns null on plain HTTP
 * (local dev on a non-https host). The caller omits the key rather than sending
 * a broken one, and never lets a hashing failure cost the whole event.
 *
 * @param {string} email - Raw submitted address
 * @returns {Promise<string|null>} Lowercase hex digest, or null when unavailable
 */
export const hashEmail = async (email) => {
	const normalised = String(email || '')
		.trim()
		.toLowerCase()

	if (!normalised) return null

	try {
		if (!window.crypto?.subtle) return null

		const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalised))

		return Array.from(new Uint8Array(digest))
			.map((byte) => byte.toString(16).padStart(2, '0'))
			.join('')
	} catch {
		return null
	}
}

/**
 * Snapshot every attribution value readable at this moment.
 *
 * Keys always present, null when unknown, so the payload stays diffable against
 * the equivalent event charm sends from the app frontend.
 *
 * @returns {Object} Attribution properties for a tracking payload
 */
export const collectAttribution = () => {
	const params = new URLSearchParams(window.location.search)
	const ids = readMarketingIds()

	const attribution = {
		path: getPath(),
		segmentAnonymousId: readSegmentAnonymousId(),
		googleClientID: readCookie('_ga') || ids.google?.client_id || null,
	}

	CLICK_IDS.forEach(({ key, param, network }) => {
		attribution[key] = params.get(param) || ids[network]?.click_id || null
	})

	attribution.initialReferrer = document.referrer || null
	attribution.utmParams = getUtmParams(params)

	return attribution
}

/**
 * Parse the Iterable list id out of a form action.
 * Yields null for any form posting somewhere that isn't Iterable.
 * @param {string} action - Absolute or relative form action URL
 * @returns {string|null} publicIdString value or null
 */
export const getFormName = (action) => {
	try {
		return new URL(action, window.location.href).searchParams.get('publicIdString')
	} catch {
		return null
	}
}
